import type {
  GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodeClientAdminsUserBulkCreationRequests200,
  GetApiV1ThreeLetterCodeClientAdminsUsers200One,
} from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useTooltip } from '@fukuri-admins/ui/common/Tooltip/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { getClientName, getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetClientAdminsPartnerFeatureFlags } from '~/hooks';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { useGetUsersCsv } from '../features/UserBulkCreation/hooks/useGetUsersCsv';
import { fetchActivationCodesCsv } from '../logics';
import type { UserListPresentation } from '../presentations';
import { useCancelUserBulkCreationRequest } from './useCancelUserBulkCreationRequest';
import { useDeleteUser } from './useDeleteUser';
import { useGetUserBulkCreationRequests } from './useGetUserBulkCreationRequests';
import { useGetUsers } from './useGetUsers';

type SelectedTab = 'users' | 'uploadStatus';

type UsersSortState = {
  key: 'no' | 'email' | 'name' | 'branchName' | 'branchCode' | 'createdAt' | null;
  direction: 'asc' | 'desc';
};

type UploadStatusSortState = {
  key: 'status' | 'receptNo' | 'scheduledAt' | 'registeredCount' | null;
  direction: 'asc' | 'desc';
};

type PresentationProps = React.ComponentProps<typeof UserListPresentation>;

type IUseUserList = () => {
  state: {
    /**
     * 事業所未設定の利用者数（新 Alert 用）
     *
     * URL クエリパラメータ `unsetBranchUserCount` の値。1 以上の場合のみ新 Alert を表示する。
     */
    unsetBranchUserCount: number;
    /** 環境フィーチャーフラグ（staging / development 環境で true） */
    enableStaging: boolean;
    /**
     * PartnerFeatureFlag 取得レスポンス（Suspense により取得済みが保証される）。
     * usersData / bulkCreationData と同様、フックは取得レスポンスをそのまま渡し、
     * フィールドの取り出し・表示可否の導出は logics/convertToPresentationProps 側で行う。
     */
    partnerFeatureFlagData: GetApiV1ThreeLetterCodeClientAdminsPartnerFeatureFlags200;
    selectedTab: SelectedTab;
    nameValue: string;
    emailValue: string;
    usersData: GetApiV1ThreeLetterCodeClientAdminsUsers200One;
    bulkCreationData: GetApiV1ThreeLetterCodeClientAdminsUserBulkCreationRequests200;
    currentPage: number;
    totalPage: number;
    totalCount: number;
    uploadStatusCurrentPage: number;
    uploadStatusTotalPage: number;
    uploadStatusTotalCount: number;
    isBulkCreationModalOpen: boolean;
    isBulkDeletionModalOpen: boolean;
    usersSortState: UsersSortState;
    uploadStatusSortState: UploadStatusSortState;
    tooltipId: PresentationProps['tooltipId'];
  };
  actions: {
    handleReload: () => void;
    handleTabChange: (value: SelectedTab) => void;
    handleOpenBulkDeletionModal: () => void;
    handleCloseBulkDeletionModal: () => void;
    handleSuccessBulkDeletion: () => void;
    handleOutputCsv: () => void;
    handleOutputActivationCodeCsv: () => Promise<void>;
    handleNavigateToUserNew: () => void;
    handleOpenBulkCreationModal: () => void;
    handleCloseBulkCreationModal: () => void;
    handleSuccessBulkCreation: () => void;
    handleNameChange: (value: string) => void;
    handleEmailChange: (value: string) => void;
    handleSearch: () => void;
    handleReset: () => void;
    handleDetailClick: PresentationProps['onDetailClick'];
    handleSortUsers: PresentationProps['onSortUsers'];
    handleEditUser: PresentationProps['onEditUser'];
    handleDeleteUser: PresentationProps['onDeleteUser'];
    handleCopyActivationCode: (code: string) => Promise<void>;
    handlePageChange: (page: number) => void;
    handleSortUploadStatus: PresentationProps['onSortUploadStatus'];
    handleUploadStatusPageChange: (page: number) => void;
    handleCancelReservation: PresentationProps['onCancelReservation'];
    handleShowTooltip: PresentationProps['showTooltip'];
    handleHideTooltip: PresentationProps['hideTooltip'];
  };
};

/**
 * 利用者一覧 新UI の状態管理とアクションを提供するコンポーネントフック
 *
 * - URL クエリパラメータ（page / email / full_name）で検索・ページング状態を管理する
 * - committedFilterParams を SWR キーにすることで、検索ボタン押下時のみ API が発火する
 * - ソート状態は useState で管理する（TODO: LEA-2039 で API パラメータに反映する）
 */
export const useUserList: IUseUserList = () => {
  const navigate = useNavigate();
  const { enableStaging } = getEnvFeatureFlag();
  const threeLetterCode = getThreeLetterCode() ?? '';
  // 取得のみを担い、レスポンスからの取り出し・表示可否（showInternalColumns /
  // showDownloadActivationCode）の導出は logics/convertToPresentationProps 側に委譲する。
  // suspense: true（デフォルト）のため data は取得済みが保証される。
  const { data: partnerFeatureFlagData } = useGetClientAdminsPartnerFeatureFlags({
    threeLetterCode,
  });
  const [filterParams, setFilterParams] = useSearchParams();
  const [isBulkCreationModalOpen, setIsBulkCreationModalOpen] = useState(false);
  const [isBulkDeletionModalOpen, setIsBulkDeletionModalOpen] = useState(false);

  // committedFilterParams（useState）: useGetUsers に渡す API 用パラメータ。
  // 検索ボタン押下・クリア・ページ変更時のみ更新することで「検索ボタン押下時のみ API 発火」を実現する。
  const [committedFilterParams, setCommittedFilterParams] = useState(
    () => new URLSearchParams(filterParams)
  );

  const [selectedTab, setSelectedTab] = useState<SelectedTab>('users');
  const [usersSortState, setUsersSortState] = useState<UsersSortState>({
    key: null,
    direction: 'asc',
  });
  const [uploadStatusSortState, setUploadStatusSortState] = useState<UploadStatusSortState>({
    key: null,
    direction: 'asc',
  });

  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();
  const { showTooltip, hideTooltip, tooltipId } = useTooltip();

  const { trigger: downloadUsersCsv } = useGetUsersCsv(threeLetterCode, {
    onSuccess: blob => downloadFile(blob, 'users.csv'),
    onError: () => showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' }),
  });

  const {
    data: usersData,
    totalPage,
    totalCount,
    mutate,
  } = useGetUsers({
    threeLetterCode,
    filter: {
      email: committedFilterParams.get('email') || undefined,
      fullName: committedFilterParams.get('full_name') || undefined,
    },
    page: committedFilterParams.get('page') || undefined,
  });

  const {
    data: bulkCreationData,
    totalPage: uploadStatusTotalPage,
    totalCount: uploadStatusTotalCount,
    mutate: mutateBulkCreationRequests,
  } = useGetUserBulkCreationRequests({
    threeLetterCode,
    page: committedFilterParams.get('upload_status_page') || undefined,
  });

  const { trigger: deleteUser } = useDeleteUser(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '利用者を削除しました。', status: 'success' });
      mutate();
    },
    onError: () => {
      showToast({ message: '削除に失敗しました。', status: 'danger' });
    },
  });

  const { trigger: cancelReservation } = useCancelUserBulkCreationRequest(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '予約を取り消しました。', status: 'success' });
      mutateBulkCreationRequests();
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({ message: errors[0], status: 'danger' });
          return;
        }
      }
      showToast({ message: '予約の取り消しに失敗しました。', status: 'danger' });
    },
  });

  // URL クエリパラメータから検索フォームの値を導出する
  const nameValue = filterParams.get('full_name') || '';
  const emailValue = filterParams.get('email') || '';

  const currentPage = Number(committedFilterParams.get('page')) || 1;

  /**
   * 事業所未設定の利用者数（新 Alert 用）
   *
   * URL クエリパラメータ `unsetBranchUserCount` から取得する。
   * 値が 1 以上の整数でない場合は 0 として扱い、新 Alert を表示しない。
   */
  const unsetBranchUserCountRaw = filterParams.get('unsetBranchUserCount');
  const unsetBranchUserCountParsed = Number.parseInt(unsetBranchUserCountRaw ?? '', 10);
  const unsetBranchUserCount =
    Number.isFinite(unsetBranchUserCountParsed) && unsetBranchUserCountParsed >= 1
      ? unsetBranchUserCountParsed
      : 0;

  /**
   * 予約中アラートの「再読み込みして確認」押下時のハンドラ
   */
  const handleReload = () => {
    mutateBulkCreationRequests();
  };

  /**
   * タブ切り替え時のハンドラ
   *
   * タブ切り替え時に URL クエリパラメータを全初期化する
   *
   * @param value 切り替え先のタブ値
   */
  const handleTabChange = (value: SelectedTab) => {
    setSelectedTab(value);
    const newParams = new URLSearchParams();
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * CSV 一括削除モーダルを開くハンドラ
   */
  const handleOpenBulkDeletionModal = () => {
    setIsBulkDeletionModalOpen(true);
  };

  /**
   * CSV 一括削除モーダルを閉じるハンドラ
   */
  const handleCloseBulkDeletionModal = () => {
    setIsBulkDeletionModalOpen(false);
  };

  /**
   * CSV 一括削除成功時のハンドラ
   *
   * モーダルを閉じてアップロード状況を再取得する
   */
  const handleSuccessBulkDeletion = () => {
    setIsBulkDeletionModalOpen(false);
    mutateBulkCreationRequests();
  };

  /**
   * 利用者一覧 CSV ダウンロード時のハンドラ
   */
  const handleOutputCsv = () => {
    downloadUsersCsv();
  };

  /**
   * アクティベーションコード CSV ダウンロード時のハンドラ
   */
  const handleOutputActivationCodeCsv = async () => {
    try {
      const blob = await fetchActivationCodesCsv(threeLetterCode);
      const clientName = getClientName() || 'activation_codes';
      downloadFile(blob, `${clientName}.csv`);
    } catch {
      showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' });
    }
  };

  /**
   * 新規追加ボタン押下時のハンドラ
   *
   * 利用者新規追加ページへ遷移する
   */
  const handleNavigateToUserNew = () => {
    navigate(ROUTES.USER_NEW);
  };

  /**
   * CSV 一括追加モーダルを開くハンドラ
   */
  const handleOpenBulkCreationModal = () => {
    setIsBulkCreationModalOpen(true);
  };

  /**
   * CSV 一括追加モーダルを閉じるハンドラ
   */
  const handleCloseBulkCreationModal = () => {
    setIsBulkCreationModalOpen(false);
  };

  /**
   * CSV 一括追加成功時のハンドラ
   *
   * モーダルを閉じてアップロード状況を再取得する
   */
  const handleSuccessBulkCreation = () => {
    setIsBulkCreationModalOpen(false);
    mutateBulkCreationRequests();
  };

  /**
   * 名前検索フォームの変更時のハンドラ
   *
   * URL の full_name パラメータを replace: true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleNameChange = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('full_name', value);
    } else {
      newParams.delete('full_name');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * メールアドレス検索フォームの変更時のハンドラ
   *
   * URL の email パラメータを replace: true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleEmailChange = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('email', value);
    } else {
      newParams.delete('email');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 検索ボタン押下時のハンドラ
   *
   * filterParams を committedFilterParams にコピーして API を発火させる
   */
  const handleSearch = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 条件リセットボタン押下時のハンドラ
   *
   * email / full_name / page パラメータをすべて削除し、API を再発火する
   */
  const handleReset = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('email');
    newParams.delete('full_name');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * ユーザーテーブルのソートキー押下時のハンドラ
   *
   * 同じキーを押下すると direction をトグル、異なるキーなら asc にリセットする
   *
   * TODO: LEA-2039 で API パラメータに反映する
   *
   * @param key ソート対象のキー
   */
  const handleSortUsers: PresentationProps['onSortUsers'] = key => {
    setUsersSortState(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  /**
   * 行クリック時のハンドラ。利用者詳細画面に遷移する。
   *
   * @param userId 詳細表示対象の利用者 ID
   */
  const handleDetailClick: PresentationProps['onDetailClick'] = userId => {
    navigate(replaceRouteParams(ROUTES.USER_SHOW, { id: userId }));
  };

  /**
   * ユーザーテーブルの「編集」押下時のハンドラ
   *
   * 利用者編集画面 `/users/:id/edit` へ遷移する
   *
   * @param userId 操作対象の利用者 ID
   */
  const handleEditUser: PresentationProps['onEditUser'] = userId => {
    navigate(replaceRouteParams(ROUTES.USER_EDIT, { id: userId }));
  };

  /**
   * ユーザーテーブルの「削除」押下時のハンドラ
   *
   * @param userId 操作対象の利用者 ID
   */
  const handleDeleteUser: PresentationProps['onDeleteUser'] = userId => {
    const user = usersData.users.find(u => u.id === userId);
    if (!user) return;

    showAlertDialog({
      title: `${user.full_name}を削除しますか？`,
      message: '削除すると元に戻すことはできません。',
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deleteUser(userId);
      },
    });
  };

  /**
   * アクティベーションコードをクリップボードにコピーするハンドラ
   *
   * 成功時はコピー完了トーストを表示し、失敗時はエラートーストを表示する。
   *
   * @param code コピーするアクティベーションコード
   */
  const handleCopyActivationCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      showToast({
        message: 'アクティベーションコードをコピーしました',
        status: 'success',
      });
    } catch {
      showToast({
        message: 'コピーに失敗しました',
        status: 'danger',
      });
    }
  };

  /**
   * ユーザーテーブルのページ変更時のハンドラ
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param page 遷移先のページ番号（1-indexed）
   */
  const handlePageChange = (page: number) => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('page', String(page));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', String(page));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * アップロード状況テーブルのソートキー押下時のハンドラ
   *
   * 同じキーを押下すると direction をトグル、異なるキーなら asc にリセットする
   *
   * @param key ソート対象のキー
   */
  const handleSortUploadStatus: PresentationProps['onSortUploadStatus'] = key => {
    setUploadStatusSortState(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  /**
   * アップロード状況テーブルのページ変更時のハンドラ
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param page 遷移先のページ番号（1-indexed）
   */
  const handleUploadStatusPageChange = (page: number) => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('upload_status_page', String(page));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('upload_status_page', String(page));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * アップロード状況テーブルの「予約を取り消す」ボタン押下時のハンドラ
   *
   * @param id 操作対象の一括登録リクエスト ID
   */
  const handleCancelReservation: PresentationProps['onCancelReservation'] = id => {
    const request = bulkCreationData.user_bulk_creation_requests.find(r => r.id === id);
    if (!request) return;

    showAlertDialog({
      title: `【受付番号】${request.id}の予約を取り消しますか？`,
      message:
        '予約中のデータは削除されます。必要に応じてCSVファイルをアップロードし直してください。',
      confirmText: '取り消す',
      cancelText: '閉じる',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await cancelReservation(id);
      },
    });
  };

  return {
    state: {
      unsetBranchUserCount,
      enableStaging,
      partnerFeatureFlagData,
      selectedTab,
      nameValue,
      emailValue,
      usersData,
      bulkCreationData,
      currentPage,
      totalPage,
      totalCount,
      uploadStatusCurrentPage: Number(committedFilterParams.get('upload_status_page')) || 1,
      uploadStatusTotalPage,
      uploadStatusTotalCount,
      isBulkCreationModalOpen,
      isBulkDeletionModalOpen,
      usersSortState,
      uploadStatusSortState,
      tooltipId,
    },
    actions: {
      handleReload,
      handleTabChange,
      handleOpenBulkDeletionModal,
      handleCloseBulkDeletionModal,
      handleSuccessBulkDeletion,
      handleOutputCsv,
      handleOutputActivationCodeCsv,
      handleNavigateToUserNew,
      handleOpenBulkCreationModal,
      handleCloseBulkCreationModal,
      handleSuccessBulkCreation,
      handleNameChange,
      handleEmailChange,
      handleSearch,
      handleReset,
      handleDetailClick,
      handleSortUsers,
      handleEditUser,
      handleDeleteUser,
      handleCopyActivationCode,
      handlePageChange,
      handleSortUploadStatus,
      handleUploadStatusPageChange,
      handleCancelReservation,
      handleShowTooltip: showTooltip,
      handleHideTooltip: hideTooltip,
    },
  };
};
